import classNames from "classnames";
import FieldError from "pages/_components/fields/FieldError";
import FieldLabel from "pages/_components/fields/FieldLabel";
import withFocus from "pages/_components/withFocus";
import Select from "pages/forms/_components/_fields/Select";
import { arrayOf, bool, func, number, oneOfType, shape, string } from "prop-types";
import React, { Component } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { selectors as i18nSelectors } from "reducers/i18n";
import { compose } from "redux";
import { getInteger } from "util/config";
import { countDecimalPlaces, numberFormat } from "util/number";

export class AmountField extends Component {
    static propTypes = {
        clearable: bool,
        disableSelect: bool,
        disabled: bool,
        hideLabel: bool,
        hideCurrency: bool,
        labelText: string,
        idForm: string,
        name: string.isRequired,
        optional: string,
        isFocused: bool.isRequired,
        hasError: oneOfType([bool, shape({})]),
        lang: string.isRequired,
        labelNoMarginTop: string,
        maximumDecimals: number.isRequired,
        minimumDecimals: number.isRequired,
        toggleIsFocused: func.isRequired,
        onCurrencyChange: func.isRequired,
        onInputChange: func.isRequired,
        onBlur: func.isRequired,
        size: string,
        error: string,
        currency: string,
        amount: oneOfType([string, number]),
        maxLength: oneOfType([string, number]),
        data: shape({
            options: arrayOf(
                shape({
                    id: oneOfType([string, number]).isRequired,
                    label: string.isRequired,
                }),
            ),
        }),
        field: shape({}),
        bold: bool,
        relativeStyleError: bool,
    };

    static defaultProps = {
        clearable: false,
        disableSelect: false,
        disabled: false,
        hideLabel: false,
        hideCurrency: false,
        labelText: null,
        labelNoMarginTop: null,
        idForm: "",
        size: "",
        error: "",
        optional: "",
        hasError: false,
        amount: "",
        currency: "",
        maxLength: "20",
        data: {},
        field: {},
        bold: true,
        relativeStyleError: false,
    };

    renderLabel = () => {
        const { idForm, name, labelText, optional, labelNoMarginTop } = this.props;
        const id = `${idForm}.${name}`;
        if (labelText !== null) {
            return (
                <FieldLabel
                    labelText={labelText}
                    optional={optional}
                    idField={id}
                    labelNoMarginTop={labelNoMarginTop}
                />
            );
        }

        return (
            <FieldLabel
                labelKey={`${idForm}.${name}.label`}
                optional={optional}
                idField={id}
                labelNoMarginTop={labelNoMarginTop}
            />
        );
    };

    render() {
        /* eslint-disable react/prop-types */
        const {
            field,
            idForm,
            clearable,
            data,
            disableSelect,
            disabled,
            hideLabel,
            isFocused,
            lang,
            maximumDecimals,
            minimumDecimals,
            toggleIsFocused,
            hideCurrency,
            size,
            hasError,
            onCurrencyChange,
            onInputChange,
            currency,
            amount,
            maxLength,
            onBlur,
            error,
            bold,
            relativeStyleError,
        } = this.props;
        /* eslint-enable react/prop-types */

        let options;
        if (!hideCurrency) {
            ({ options } = data);
        }
        const { decimalSeparator, thousandSeparator } = numberFormat(lang);
        const decimalPlaces = this.amountRef ? countDecimalPlaces(this.amountRef.value, decimalSeparator) : 0;
        const decimalScale = Math.max(Math.min(decimalPlaces, maximumDecimals), minimumDecimals);
        const idField = `${idForm}.${field.name}`;

        return (
            <div
                className={classNames("form-group", "form-group--composite", {
                    "has-error": hasError,
                    "has-focus": isFocused,
                    "form-group--small": size === "small",
                })}
                onFocus={toggleIsFocused}>
                {!hideLabel && this.renderLabel()}
                <div
                    className={classNames("input-group", {
                        "is-disabled": disabled,
                    })}>
                    {!hideCurrency && options.length === 1 ? (
                        <span
                            className={classNames("currency pr-11", {
                                "is-disabled": !disabled,
                            })}>
                            {options[0].label}
                        </span>
                    ) : (
                        !hideCurrency && (
                            <Select
                                clearable={clearable}
                                containerClassName="currency-selector"
                                disabled={disableSelect}
                                onChange={onCurrencyChange}
                                options={options.map(({ id, label }) => ({ value: id, label }))}
                                searchable={false}
                                value={currency}
                                labelKey="label"
                                valueKey="id"
                                onBlur={() => {
                                    toggleIsFocused();
                                    onBlur();
                                }}
                            />
                        )
                    )}
                    <NumberFormat
                        name={field.name}
                        id={idField}
                        allowNegative={false}
                        className={classNames("form-control", {
                            "only-input": hideCurrency,
                            "text-bold font-size-6": bold,
                        })}
                        decimalScale={decimalScale}
                        decimalSeparator={decimalSeparator}
                        maxLength={maxLength}
                        onChange={onInputChange}
                        thousandSeparator={thousandSeparator}
                        type="text"
                        onBlur={() => {
                            toggleIsFocused();
                            onBlur();
                        }}
                        value={amount}
                        disabled={disabled}
                    />
                </div>
                {hasError && <FieldError error={error} relativeStyleError={relativeStyleError} />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    lang: i18nSelectors.getLang(state),
    maximumDecimals: getInteger("defaultDecimal.maximum"),
    minimumDecimals: getInteger("defaultDecimal.minimum"),
});

export default compose(connect(mapStateToProps), withFocus)(AmountField);
